import React, { useMemo } from 'react';
import { Card, CardActionArea, CardContent, createTheme, Grid, ThemeProvider } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { App } from 'models/app';
import { getStarRating, truncateString } from '../../utils/helpers';
import styles from './styles.module.scss';
import { StarRating } from '../StarRating';
import { Link as RouteLink } from 'react-router-dom';
import { generateRoute } from '../../utils/location';
import { AppIcon } from '../Common/AppIcon';
import {
  appHasTrialOption,
  getLocalizedAppDetails,
  getSubscription,
  subscriptionWithLowestPlan,
} from '../../component-helpers/app-details';
import { MAX_DESC_STRING_LEN } from '../../utils/constants';
import commonStyles from '../../styles/commonStyles.module.scss';
import { useRatingsAndReviews } from '../../contexts/providers/ratings-reviews';
import { canShowStarRating } from '../../component-helpers/app-reviews';
import { AppMarketPageIdentifier } from '../../AppRoutes';
import { convertToSentenceCase } from '../../utils/transformationHelper';
import ctx from 'classnames';

export const AppCard: React.FC<{ app: App }> = ({ app }) => {
  const { id, developer, aggregateRating } = app;
  const context = useRatingsAndReviews();
  const { t, i18n: {language} } = useTranslation(),
    rating = getStarRating(aggregateRating);
  const { freeVersionText, formattedSubscriptionArr } = getSubscription(
    app
  );

  const appHasTrial = appHasTrialOption(app);

  let { name, description, tagline, filenameIconSmall } = useMemo(() => getLocalizedAppDetails(app, language), [app, language]);
  description = tagline?.trim() || description?.trim() || '';

  if(description) {
    let doc = new DOMParser().parseFromString(description, 'text/html');
    description = doc.body.textContent?.trim() || "";
  }

  const canShowRatings = canShowStarRating(id, developer.id, aggregateRating.reviewCount, context);

  const subscriptionWithLowestPlans = subscriptionWithLowestPlan(formattedSubscriptionArr) || [];

  const theme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 450,
        md: 920,
        lg: 1200,
        xl: 1536,
      },
    },
  });

  if(name) {
    const nameArr = [...name];
    for(var i = 0; i < nameArr.length; i++) {
      nameArr[i] = nameArr[i].replace(/_/g, '_<wbr/>');
    }
    name = nameArr.join('');
  }

  return (
    <ThemeProvider theme={theme}>
    <Card elevation={1} square>
      <CardActionArea
        className={styles.card}
        disableRipple
        component={RouteLink}
        to={generateRoute(AppMarketPageIdentifier.AppDetails, id)}
        style={{ textDecoration: 'none' }}
        role="link"
        tabIndex={0}
      >
        <CardContent className={styles['card-content']}>
          <Grid container >
            <Grid lg={3} md={6} xs={6} sm={4}>
              <div tabIndex={-1}>
                <AppIcon src={filenameIconSmall}/>
              </div>
            </Grid>
            <Grid lg={9} md={6} xs={6} sm={8}>
              <div className={styles.body}>
                <h2 className={styles.title} aria-label={t('aria.app_card',{name:name})} dangerouslySetInnerHTML={{__html: name || ""}}></h2>
                <RouteLink to={generateRoute(AppMarketPageIdentifier.DeveloperApps, developer.id)} role='link' aria-label={t('aria.developer_name',{developerName:developer.name})} >
                  <p className={ctx(styles.developerName, commonStyles.link)} >{t('by_developer', { name: developer.name })}</p>
                </RouteLink>
              </div>
            </Grid>
            <Grid xs={1} lg={3} md={6} sm={4} className={styles.structuralItem}>
              <div></div>
            </Grid>
            <Grid xs={11} lg={9} md={6} sm={8} className={styles.descriptionItem}>
              <div className={styles.body}>
                {description && (
                  <p className={styles.description} role='note' aria-label={t('aria.app_description',{description:description})}>{truncateString(description, MAX_DESC_STRING_LEN)}</p>
                )}
                {canShowRatings && <StarRating rating={rating} size={'small'} />}
                <div className={styles.availablePlans}>
                  {freeVersionText && (
                    <p><strong>{convertToSentenceCase(freeVersionText.freePlan)}</strong></p>
                  )}
                  {appHasTrial && (
                    <div className={styles.uppercase}>
                      <p><strong>{t('app_details.trial_available')}</strong></p>
                    </div>
                  )}
                  {subscriptionWithLowestPlans.length > 0 && (
                    <p><strong>{t('app_details.subscription_pricing')}</strong></p>
                  )}
                </div>
              </div>
            </Grid>
          </Grid>
        </CardContent>
      </CardActionArea>
    </Card>
    </ThemeProvider>
  );
};
